import capitalize from 'lodash/capitalize';
import React, { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@material-ui/core';
import { PayoutOptions } from '../../util/constants';
import { formatDollarAmountAsCurrency } from '../../util/currency';
import { TypographyWrapper } from '..';
import { TypographyWeight } from '../TypographyWrapper/TypographyWrapper';
import { PayoutValues } from '../ManageBundlesContainer/manageBundlesContainerUtils';
import BundlePanelInfoTooltip from './BundlePanelInfoTooltip';

interface PayoutOptionProps {
  payoutValues: PayoutValues;
  payoutOption: PayoutOptions;
  creditCode?: {
    amount: number;
    currency: string;
  } | null;
}

const StyledProceedsText = ({
  bold = false,
  dynamic = false,
  paragraph = false,
  children,
}: {
  bold?: boolean;
  dynamic?: boolean;
  paragraph?: boolean;
  children: ReactNode;
}) => (
  <TypographyWrapper
    variant="body1"
    weight={bold ? TypographyWeight.Bold : undefined}
    isDynamicValue={dynamic}
    component={paragraph ? 'p' : 'span'}
  >
    {children}
  </TypographyWrapper>
);

const ShippingReimbursementTooltip: FC<{ value: string }> = ({ value }) => (
  <Box ml={0.5}>
    <TypographyWrapper variant="body2" component="span">
      <BundlePanelInfoTooltip
        title={
          <>
            <TypographyWrapper variant="body2" component="span">
              Includes{' '}
            </TypographyWrapper>
            <TypographyWrapper variant="body2" component="span" isDynamicValue>
              {value}
            </TypographyWrapper>
            <TypographyWrapper variant="body2" component="span">
              {' '}
              shipping reimbursement
            </TypographyWrapper>
          </>
        }
      />
    </TypographyWrapper>
  </Box>
);

const CalculatedProceeds: FC<PayoutOptionProps> = ({ payoutValues, payoutOption, creditCode }) => {
  const intl = useIntl();
  const issuedCredit =
    creditCode && formatDollarAmountAsCurrency(intl, creditCode.amount, creditCode.currency);

  if (!payoutValues[payoutOption]) {
    return null;
  }

  // Use payout value with shipping if it exists, otherwise use the regular payout value
  const payoutValue =
    payoutValues.shippingReimbursementInfo?.payoutValuesWithShipping[payoutOption] ??
    payoutValues[payoutOption];

  if (payoutOption === PayoutOptions.Credit && payoutValues.creditInBrandShopifyCurrency) {
    return (
      <>
        <StyledProceedsText dynamic>{payoutValue}</StyledProceedsText> Credit (issued as{' '}
        <StyledProceedsText dynamic>
          {issuedCredit || payoutValues.creditInBrandShopifyCurrency}
        </StyledProceedsText>
        )
      </>
    );
  }
  if (payoutOption === PayoutOptions.Credit) {
    return (
      <>
        <StyledProceedsText dynamic>{issuedCredit || payoutValue}</StyledProceedsText> Credit
      </>
    );
  }
  if (payoutOption === PayoutOptions.Cash) {
    return (
      <>
        <StyledProceedsText dynamic>{payoutValue}</StyledProceedsText> Cash
      </>
    );
  }
  return (
    <>
      <StyledProceedsText dynamic>{payoutValue}</StyledProceedsText>{' '}
      <StyledProceedsText dynamic>{capitalize(payoutOption)}</StyledProceedsText>
    </>
  );
};

const BundleProceedsText: FC<PayoutOptionProps> = ({ payoutValues, payoutOption, creditCode }) => (
  <Box display="flex" flexDirection="row" alignItems="center">
    <StyledProceedsText paragraph>
      <StyledProceedsText bold>Proceeds: </StyledProceedsText>
      <CalculatedProceeds
        payoutValues={payoutValues}
        payoutOption={payoutOption}
        creditCode={creditCode}
      />
    </StyledProceedsText>
    {payoutValues.shippingReimbursementInfo && (
      <ShippingReimbursementTooltip value={payoutValues.shippingReimbursementInfo.formattedValue} />
    )}
  </Box>
);

export default BundleProceedsText;
